import { isXssSafeUrl } from './UrlUtils';

/**
 * Converts a fetch response to a Blob
 */
const createBlob = (response: Response): Promise<Blob> => response.blob();

/**
 * TODO: Unused for now. To be removed later.
 * Converts a Blob to base64 string
 */
const fromBlobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
    reader.readAsDataURL(blob);
  });

/**
 * Image metadata for Excel export
 */
export interface ExcelImageMetadata {
  base64: string;
  imageType: string;
  url: string;
}

//  TODO: Unused for now. To be removed later.
export const translateImage = (url: string): Promise<string> =>
  fetch(url)
    .then(createBlob)
    .then((blob) => fromBlobToBase64(blob))
    .catch((error) => {
      console.error(`Failed to convert image from URL: ${url}`, error);
      return ''; // Return empty string on error
    });

export const generateBase64Image = (url: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = document.createElement('img');
    img.crossOrigin = 'anonymous'; // Important for CORS

    // Add cache buster to avoid pulling a CORS-less cached version
    const separator = url.includes('?') ? '&' : '?';
    img.src = `${url}${separator}cb=${new Date().getTime()}`;

    img.onload = () => {
      try {
        // create canvas to resize the image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // calculate new dimensions maintaining aspect ratio
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);

        // convert to base64 string
        const resizedBase64 = canvas.toDataURL('image/png');
        resolve(resizedBase64);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
  });

/**
 * Converts an array of image URLs to base64 format with optional URL resolution via AwContentService.
 *
 * @param imageUrls - Array of image URL strings to convert
 * @param awContentService - AwContentService instance (optional) for URL resolution via getAssetPath
 * @returns Promise resolving to array of objects containing base64, imageType, and original url
 * The returned base64 should never be directly injected into the DOM as an inline HTML
 */
export const convertImageUrlsToBase64 = async (
  imageUrls: string[],
  awContentService?: { getAssetPath: (assetPath: string) => string } | null
): Promise<Array<{ base64: string; url: string } | null>> => {
  const base64Promises = imageUrls.map(async (url) => {
    // Use awContentService to resolve the full URL with proper proxy/domain handling
    const resolvedUrl = awContentService?.getAssetPath(url) ?? url;
    if (isXssSafeUrl(resolvedUrl)) {
      try {
        const base64 = await generateBase64Image(resolvedUrl);
        if (base64) {
          return { base64, url };
        }
      } catch (error) {
        console.error(`Failed to convert image from URL: ${resolvedUrl}`, error);
      }
    } else {
      console.error(`Image URL is not safe for use: ${resolvedUrl}`);
    }
    return null;
  });

  return Promise.all(base64Promises);
};
